import { saveUserSettings, IUserSettingsAction } from '../userSettings';
import { Dispatch } from 'redux';
import { TGetRootState, TDispatchResult } from 'types/shared';

const ADD_FOLLOWING_ENTITY = 'opus/user/following/ADD_FOLLOWING_ENTITY';
const REMOVE_FOLLOWING_ENTITY = 'opus/user/following/REMOVE_FOLLOWING_ENTITY';

export type FollowingActionTypes =
  | typeof ADD_FOLLOWING_ENTITY
  | typeof REMOVE_FOLLOWING_ENTITY;

export default function reducer(
  state: any[],
  action: IUserSettingsAction
): any[] {
  switch (action.type) {
    case ADD_FOLLOWING_ENTITY: {
      const { entityId, entityType } = action.payload;

      return state.find(
        (item) => item.id === entityId && item.type === entityType
      )
        ? state
        : [
            ...state,
            {
              id: entityId,
              type: entityType,
            },
          ];
    }

    case REMOVE_FOLLOWING_ENTITY: {
      const { entityId, entityType } = action.payload;

      return [
        ...state.filter(
          (item) => !(item.id === entityId && item.type === entityType)
        ),
      ];
    }

    default:
      return state;
  }
}

const handleDispatch = (
  func: (dispatch: Dispatch) => any
): TDispatchResult<any> => {
  return (dispatch: Dispatch, getState: TGetRootState): any => {
    // If getState().userSettings.success.all is false, this means we encountered an error while loading the
    // userSettings endpoint. It's safest not to overwrite this with the current Redux store anymore.
    if (getState().userSettings.success.all) {
      const originalState = getState().userSettings.data.followingEntities;

      func(dispatch);

      return dispatch(
        saveUserSettings('followingEntities', originalState) as any
      );
    }
    // eslint-disable-next-line no-console
    console.warn(
      'We are not saving your new globalFilters to the KV store because we could not retrieve them and do not want to run the risk of corrupting data.'
    );
  };
};

const toggle = (entity: any, isFollowing: boolean): any => {
  const type = isFollowing ? REMOVE_FOLLOWING_ENTITY : ADD_FOLLOWING_ENTITY;

  return {
    type,
    payload: {
      entityType: entity.type,
      entityId: entity.id,
    },
  };
};

export const toggleFollowingEntity = (
  entity: any,
  isFollowing: boolean
): TDispatchResult<any> => {
  const toggleFollowings = (dispatch: Dispatch): TDispatchResult<any> =>
    dispatch(toggle(entity, isFollowing));
  return handleDispatch(toggleFollowings);
};

/** Removes multiple entities from the followingEntities */
export const removeMultipleFollowingEntities = (
  entityType: string,
  entityIds: string[]
): TDispatchResult<any> => {
  return (dispatch: Dispatch, getState: TGetRootState): any => {
    // If getState().userSettings.success.all is false, this means we encountered an error while loading the
    // userSettings endpoint. It's safest not to overwrite this with the current Redux store anymore.
    if (getState().userSettings.success.all) {
      const originalState = getState().userSettings.data.followingEntities;

      entityIds.forEach((entityId) =>
        dispatch({
          type: REMOVE_FOLLOWING_ENTITY,
          payload: {
            entityType,
            entityId,
          },
        })
      );

      return dispatch(
        saveUserSettings('followingEntities', originalState) as any
      );
    }
    // eslint-disable-next-line no-console
    console.warn(
      'We are not saving your new globalFilters to the KV store because we could not retrieve them and do not want to run the risk of corrupting data.'
    );
  };
};

export const removeTypedFollowings = (entities: any): TDispatchResult<any> => {
  const dispatchRemoveFollowings = (dispatch: Dispatch): any => {
    entities.forEach(
      ({ entityId, entityType }: { entityType: string; entityId: string }) =>
        dispatch({
          type: REMOVE_FOLLOWING_ENTITY,
          payload: {
            entityId,
            entityType,
          },
        })
    );
  };

  return handleDispatch(dispatchRemoveFollowings);
};
